import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { InfoIcon } from 'lucide-react';
import { spaceOptions } from '../../data/recommender';
import { pricingConfig } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { bundleName, formatMoney, priceBundle, priceIndividual } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { QuantityStepper } from '../ui/QuantityStepper';
import { WarrantyTag } from '../ui/WarrantyTag';
export function Recommender() {
  const {
    t,
    lang
  } = useLanguage();
  const {
    startSystem,
    startIndividual
  } = useFunnel();
  const [selected, setSelected] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const option = spaceOptions.find(o => o.id === selected) ?? null;
  const matchingBundle = pricingConfig.bundles.find(b => b.qty === qty) ?? null;
  const m = (n: number) => formatMoney(n, lang);
  const choose = (id: string) => {
    const o = spaceOptions.find(x => x.id === id);
    if (!o) return;
    setSelected(id);
    setQty(o.recommendedQty);
  };
  return <div data-id="element-932" data-mp="19mzqddzpd7y5">
      <h3 className="font-display text-xl font-semibold uppercase tracking-[0.1em] text-titanium" data-id="element-933" data-mp="19oaszahew9no">{t('rec.question')}</h3>
      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5" role="radiogroup" aria-label={t('rec.question')} data-id="element-934" data-mp="1elu80clxoqgo">
        {spaceOptions.map(o => {
        const active = selected === o.id;
        return <button key={o.id} type="button" role="radio" aria-checked={active} onClick={() => choose(o.id)} className={`group relative aspect-[4/5] overflow-hidden rounded-xl border text-left transition-[border-color] duration-200 ${active ? 'border-marine' : 'mf-hairline hover:border-titanium/40'} ${o.id === 'custom' ? 'col-span-2 aspect-[8/5] md:col-span-1 md:aspect-[4/5]' : ''}`} data-id="element-935" data-mp="lvlsksis8k5s">
              <img src={o.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-opacity duration-200 group-hover:opacity-90" data-id="element-936" data-mp="3naa73gxe8otg" />
              <div className="absolute inset-0 bg-[#030A14]/55" aria-hidden data-id="element-937" data-mp="4vj1ny1q5hsv" />
              <div className="absolute inset-x-0 bottom-0 p-4" data-id="element-938" data-mp="3w3wxeriu0zv9">
                <span className="block font-display text-xl font-semibold uppercase leading-tight text-white" data-id="element-939" data-mp="38crkol86unp6">
                  {lang === 'es' ? o.labelEs : o.label}
                </span>
                <span className="mt-1 block text-xs text-titanium" data-id="element-940" data-mp="3ib76c8og54ra">{o.note}</span>
              </div>
              {active && <span className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-marine" aria-hidden data-id="element-941" data-mp="3dbkxebfniaoa" />}
            </button>;
      })}
      </div>

      <AnimatePresence mode="wait" data-id="element-942" data-mp="xn2qpyl05q2s">
        {option && <motion.div key={option.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-8 grid gap-8 rounded-2xl border mf-hairline bg-navy p-6 md:grid-cols-[1.2fr_1fr] md:p-10" aria-live="polite" data-id="element-943" data-mp="sktvevknqbxi">
            <div data-id="element-944" data-mp="26ipt0japlvli">
              <p className="text-sm text-titanium" data-id="element-945" data-mp="26i6mdyqietms">Recommended starting configuration</p>
              <p className="mt-2 font-display text-5xl font-semibold uppercase leading-none md:text-6xl" data-id="element-946" data-mp="2u80y2cz3ec5g">
                {option.recommendedQty} {option.recommendedQty === 1 ? 'Light' : 'Lights'}
              </p>
              {option.bundleId && <p className="mt-2 text-lg text-ice" data-id="element-947" data-mp="2pgiy9s8t2hm2">
                  {bundleName(pricingConfig.bundles.find(b => b.id === option.bundleId)!, lang)}
                </p>}
              <p className="mt-6 flex gap-2 text-sm leading-relaxed text-titanium" data-id="element-948" data-mp="3hx3bt9qlvkuo">
                <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-titanium/70" aria-hidden data-id="element-949" data-mp="3hz69mfy02i9y" />
                Actual lighting coverage depends on your pool's dimensions, depth, placement and the visual effect you want.
                Treat this as a starting point and adjust freely.
              </p>
            </div>

            <div className="flex flex-col" data-id="element-950" data-mp="1fbhkhkdptud7">
              <span className="text-sm font-medium" data-id="element-951" data-mp="1fcsuw31o20da">Adjust quantity</span>
              <div className="mt-3" data-id="element-952" data-mp="2c207glkb4z52">
                <QuantityStepper value={qty} onChange={setQty} min={1} max={pricingConfig.maxIndividualQty} label="lights" data-id="element-953" data-mp="2c18jnwwyc5pc" />
              </div>
              <div className="mt-6" data-id="element-954" data-mp="2396r0kt4jtpm">
                {matchingBundle ? <>
                    <div className="text-3xl font-semibold" data-id="element-955" data-mp="1yalmi96uanvd">{m(priceBundle(matchingBundle).total)}</div>
                    <div className="text-sm text-titanium" data-id="element-956" data-mp="2vphhonl6f5wr">
                      {bundleName(matchingBundle, lang)} · {m(priceBundle(matchingBundle).perUnit)} {t('common.perLight')}
                    </div>
                    <div className="mt-3" data-id="element-957" data-mp="2yap95z7ztbbx">
                      <WarrantyTag tier={matchingBundle.warranty} data-id="element-958" data-mp="2yc07hjio5qvn" />
                    </div>
                  </> : <>
                    <div className="text-3xl font-semibold" data-id="element-959" data-mp="3883kxliz1bxw">{m(priceIndividual(qty).total)}</div>
                    <div className="text-sm text-titanium" data-id="element-960" data-mp="3ca6zwk4umynx">
                      Build Your Own · {m(priceIndividual(qty).unit)} {t('common.perLight')}
                    </div>
                  </>}
              </div>
              <button type="button" onClick={() => matchingBundle ? startSystem(matchingBundle.id) : startIndividual(qty)} className={`${buttonClasses('primary')} mt-6 w-full`} data-id="element-961" data-mp="3m6js1dkqx4gk">
                {matchingBundle ? `Choose ${matchingBundle.qty}-Light System` : `${t('common.addToCart')} — ${qty} ${qty === 1 ? t('common.light') : t('common.lights')}`}
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}